import { ListItem, ListItemIcon, ListItemText, Typography } from "@mui/material";

import BaseEntitySearchAutocomplete, {
  BaseEntitySearchAutocompleteInheritableProps,
} from "@/common/components/Entity/components/BaseEntitySearchAutocomplete";
import AppIcon from "@/common/components/Icons/AppIcon";
import { renderIf } from "@/common/helpers/render/renderIf";
import { StringHelper } from "@/common/helpers/string";
import { AutocompleteOptionType, BaseAutocompleteOption } from "@/common/ts/autocomplete";
import { apiClient } from "@/core/api/ApiClient";
import {
  DamageDetectionAggregateDto,
  DamageDetectionAggregateSearchPaginatedDto,
} from "@/core/api/generated/v1";

import ContractInline from "../Contract/ContractInline";
import { getVehicleOptionTitleForOperation } from "../Vehicle/VehicleAutocomplete";
import VehicleInline from "../Vehicle/VehicleInline";

export type DamageDetectionAggregateAutocompleteOption =
  BaseAutocompleteOption<DamageDetectionAggregateDto>;

export const getDamageDetectionAggregateTitle = (data: DamageDetectionAggregateDto): string =>
  StringHelper.join(
    [data.vehicle ? getVehicleOptionTitleForOperation(data.vehicle) : data.localNumber],
    ", ",
    {
      skipEmpty: true,
    },
  );

export const damageDetectionAggregateToOption = (
  data: DamageDetectionAggregateDto,
): DamageDetectionAggregateAutocompleteOption => ({
  id: data.id!,
  title: getDamageDetectionAggregateTitle(data),
  optionType: AutocompleteOptionType.Normal,
  data: data,
});

export interface DamageDetectionAggregateAutocompleteProps extends BaseEntitySearchAutocompleteInheritableProps<DamageDetectionAggregateDto> {
  searchFilters?: Partial<
    Omit<DamageDetectionAggregateSearchPaginatedDto, "search" | "includeIds">
  >;
}

export default function DamageDetectionAggregateAutocomplete({
  searchFilters,

  isPreload = true,
  ...otherProps
}: DamageDetectionAggregateAutocompleteProps) {
  return (
    <BaseEntitySearchAutocomplete
      sx={{ minWidth: 200, flex: 1 }}
      entityToOption={damageDetectionAggregateToOption}
      isPreload={isPreload}
      request={{
        requestFunc:
          apiClient.damageDetectionAggregatesApi.apiV1DamageDetectionsAggregatesSearchPost,
        limit: 25,
        parameters: {
          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
          damageDetectionAggregateSearchPaginatedDto: {
            ...searchFilters,
          },
        },
        combineParameters: (params, newParams) => ({
          ...params,
          damageDetectionAggregateSearchPaginatedDto: {
            ...params.damageDetectionAggregateSearchPaginatedDto,
            ...newParams,
          },
        }),
        deps: [searchFilters],
      }}
      label='Damage detection aggregate'
      placeholder='Search...'
      renderOption={(props, option: DamageDetectionAggregateAutocompleteOption) => {
        return (
          <ListItem {...props}>
            <ListItemIcon>
              {renderIf()
                .if(option.optionType === AutocompleteOptionType.Normal)
                .then(<AppIcon of='damageDetectionAggregate' />)
                .elseif(option.optionType === AutocompleteOptionType.DynamicCreateNew)
                .then(<AppIcon of='add' />)
                .render()}
            </ListItemIcon>
            <ListItemText
              primary={
                <>
                  <Typography
                    variant={
                      option.optionType === AutocompleteOptionType.DynamicCreateNew
                        ? "subtitle1"
                        : "body1"
                    }
                    component='div'
                  >
                    {option.title}
                  </Typography>
                  {option.data && (
                    <Typography component='div' variant='body2'>
                      {option?.data?.vehicle && (
                        <VehicleInline entity={option.data.vehicle} withIcon={false} />
                      )}
                      {option?.data?.contract && (
                        <ContractInline entity={option.data.contract} withIcon={false} />
                      )}
                    </Typography>
                  )}
                </>
              }
            />
          </ListItem>
        );
      }}
      {...otherProps}
    />
  );
}
